/**
 * @Description: 数据可视化散点图接口，https://lbs.qq.com/webApi/visualizationApi/visualizationDoc/visualizationDocDot#5
 * @date 2022/9/6
 * @params
*/
import { Ref, WatchStopHandle } from 'vue';
import omit from 'lodash/omit';
import { LAYER_CONFIG_OMIT_PROP } from '../constants';
const reInitProps = ['enableBloom'];
const STYLE_PROPS = ['type', 'fillColor', 'strokeColor', 'strokeWidth', 'radius', 'width', 'height', 'anchor', 'src'];
export default function useDotLayer(mapInstance: LooseObject, layerInfo: Ref<MapLayer<DotLayerConfig>>) {
  if (!layerInfo.value || !layerInfo.value.config || !TMap.visualization?.Dot) return;

  const options = omit(layerInfo.value.config, LAYER_CONFIG_OMIT_PROP);

  let unwatchList: WatchStopHandle[] | null = [];

  const dotOptions = {
    ...omit(options),
    minZoom: options.zoomRange?.[0] || 3,
    maxZoom: options.zoomRange?.[1] || 20,
    type: options.type || 'circle',
    // color: options.color,
    faceTo: options.faceTo || 'screen',
    enableBloom: options.enableBloom || false,
  };
  const { imageStyle, circleStyle } = getStyleFromLayerOpts(layerInfo.value.config);
  const initLayer = () => {
    const initDotLayer = new TMap.visualization.Dot({ ...dotOptions,
      styles: { dotStyle: dotOptions.type === 'image' ? imageStyle : circleStyle } });
    initDotLayer.addTo(mapInstance);
    return initDotLayer;
  };
  let dotLayer = initLayer();
  unwatchList.push(watchEffect(() => {
    try {
      dotLayer?.setData(layerInfo.value.config.data?.map(item => ({ ...item, styleId: 'dotStyle' })));
    } catch (error) {
      dotLayer?.setData([]);
    }
  }));
  Object.keys(options).forEach((key) => {
    unwatchList?.push(watch(() => layerInfo.value.config[key], (newVal) => {
      const styleType =  layerInfo.value.config.type;
      const { imageStyle, circleStyle } = getStyleFromLayerOpts(layerInfo.value.config);
      // 部分属性没有对应set方法,需要重新初始化图层
      if (reInitProps.includes(key)) {
        !!dotLayer && dotLayer.destroy();
        dotLayer = initLayer();
        dotLayer?.setData(layerInfo.value.config.data?.map(item => ({ ...item, styleId: 'dotStyle' })));
        return;
      }
      if (STYLE_PROPS.includes(key)) {
        dotLayer.setStyles({ dotStyle: styleType === 'image' ? imageStyle : circleStyle });
        return;
      }
      dotOptionsHandlers[key](newVal, dotLayer);
    }, { deep: true }));
  });

  dotLayer.unmount = (): void => {
    dotLayer?.destroy();
    unwatchList?.forEach(unwatch => unwatch());
    unwatchList = null;
  };

  return dotLayer;
}
// 对散点层每个属性响应
const dotOptionsHandlers = {
  faceTo(val: string, layerInstance: LooseObject): void {
    layerInstance.setFaceTo(val);
  },
  zoomRange(val: DotLayerConfig['zoomRange'], layerInstance: LooseObject): void {
    layerInstance.setMinZoom(val?.[0]);
    layerInstance.setMaxZoom(val?.[1]);
  },
};
const getStyleFromLayerOpts = (layerOpts: DotLayerConfig) => {
  const { width, height, anchor, src, fillColor, strokeColor, strokeWidth, radius } = layerOpts;
  const imageStyle = {
    type: 'image',
    width,
    height,
    anchor,
    src,
  };
  if (!imageStyle.src) delete imageStyle.src; // 未传src时去掉，可用默认图，避免GL报错
  const circleStyle = {
    type: 'circle',
    fillColor,
    strokeColor,
    strokeWidth,
    radius,
  };
  return { imageStyle, circleStyle };
};
